import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { CustomFormComponent } from '@bo-client/activation-management/custom-form.component';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import { CreateFormsGrPlaceinserviceService } from '@bo-client/activation-management/grid-registration/forms/create-forms-gr-placeinservice.service';
import { p14aApplies } from '@bo-client/activation-management/grid-registration/grid-registration.utils';
import {
  ConsumerFormV2,
  ConsumerModel
} from '@bo-client/activation-management/grid-registration/model/consumer.model';
import {
  FeederConnectionStandard,
  FeedingPlantTypeEnum,
  NaProtectionTypeEnum,
  ReactivePowerProvisioningMethod,
  SymmetryConditionsEnum
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import {
  GridRegistrationModel,
  PlaceInServiceModel,
  PlaceInServiceRequestForm
} from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import {
  ProducerForm,
  ProducerModel
} from '@bo-client/activation-management/grid-registration/model/producer.model';
import { PVModuleForm } from '@bo-client/activation-management/grid-registration/model/pv-module.model';
import {
  StorageForm,
  StorageModel
} from '@bo-client/activation-management/grid-registration/model/storage.model';
import { ErrorFormCommissioningEnum } from '@bo-client/activation-management/shared/forms/create-forms-errors-model';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import { DataForCustomFormModel } from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { MetaService } from '@bo-client/core/api/meta-service';
import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';
import { ErrorsFormsService } from '@bo-client/shared/form-errors/errors-forms.service';
import { TypedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';
import { UploadForm } from '@bo-client/shared/models/documentDto';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MastrPvPlantWithLocationDto } from '@bo-client/activation-management/shared/components/mastr-validation/MastrData.dto';
import { MASTR_NUMBER_MISMATCH_TAG } from '@bo-client/activation-management/shared/components/mastr-validation/mastr-validation.component';
import { ToastrService } from 'ngx-toastr';
import { RegexToCheckModuleMaStrNumber } from '@bo-client/shared/forms/forms.validators';
import { storageTotal } from '@bo-client/activation-management/grid-registration/storage-totals.util';
import { MeteringConceptDisplayGroup } from '@bo-client/activation-management/grid-registrations/grid-registration-form-v2/form-parts/grid-registration-metering-concept-v2/grid-registration-metering-concept-v2.component';
import { ValidatorsService } from '@bo-client/shared/forms/validators.service';
import { UiAdaptionsModel } from '@bo-client/tenant-management/models/ui-adaptions.model';
import { ZerezHintRegistry } from '@bo-client/activation-management/shared/components/zerez-lookup/zerez-hint-registry';

/**
 * UI-Adaption der frei konfigurierbaren Pflicht-Bedingung am Ende des Protokolls. Das
 * Einspeiser-Protokoll hat einen eigenen Anchor, damit beide getrennt schaltbar sind.
 */
export const CUSTOM_CONDITIONS_CHECKBOX_ANCHOR =
  'gridRegistration.placeInServiceRequest.storageConditionsCheckbox';

/** Place-in-service request form for Storage grid registrations (slim vs feeder). */
@Component({
  selector: 'app-gr-place-in-service-storage-request-form',
  templateUrl: './gr-place-in-service-storage-request-form.component.html',
  styleUrls: ['./gr-place-in-service-storage-request-form.component.scss'],
  // Sammelt die ZEREZ-Hinweise dieses Formulars für die Sammelkarte oben.
  providers: [ZerezHintRegistry]
})
export class GrPlaceInServiceStorageRequestFormComponent
  implements CustomFormComponent<GridRegistrationModel, GridRegistrationModel>, OnInit
{
  private destroyRef = inject(DestroyRef);
  createFormsGrPlaceinServiceConfirmationService = inject(CreateFormsGrPlaceinserviceService);
  private createFormsGridRegistrationService = inject(CreateGridRegistrationFormsService);
  private createFormsService = inject(CreateFormsService);
  dataShareService = inject(DataShareService);
  metaService = inject(MetaService);
  private fb = inject(FormBuilder);
  private errorsFormsService = inject(ErrorsFormsService);
  private toastrService = inject(ToastrService);
  private validatorsService = inject(ValidatorsService);

  /**
   * Frei konfigurierbare Bedingung aus dem Karte "Weitere Bedingungen". Das Control liegt
   * bewusst außerhalb von `formGroup`: `confirm()` serialisiert die FormGroup über
   * `getRawValue()`, die Bestätigung wird aber nicht persistiert — der Abschluss des
   * Prozessschritts impliziert sie.
   */
  customConditionsCheckbox = new FormControl(false);

  @Input() data: DataForCustomFormModel<GridRegistrationModel>;
  @Output() onConfirm: EventEmitter<GridRegistrationModel> = new EventEmitter();

  formGroup: PlaceInServiceRequestForm;
  naProtectionTypeEnum = NaProtectionTypeEnum;
  symmetryConditionsEnum = SymmetryConditionsEnum;
  totalActivePower = 0;
  totalApparentPower = 0;
  totalModulePower = 0;
  errorFormList: ShowErrorModel[] = [];
  errorFieldRequired = 'Feld ist erforderlich';
  errorOptionRequired = 'Bitte wählen Sie eine Option aus';
  errorCheckRequired = 'Bitte bestätigen Sie die Bedingung';
  /** Platzhalter für Gesamtwerte, solange nicht alle Faktoren erfasst sind. */
  readonly totalMissingLabel = 'keine Angabe';
  model: GridRegistrationModel;

  showExternalIdField$ = of(false);
  showExternalCustomerIdField$ = of(false);

  hasProduction$: Observable<boolean>;

  formValue$: Observable<PlaceInServiceRequestForm['value']>;
  /** Modell plus aktueller Formularstand — Eingabe für die MaStR-Vergleichstabelle. */
  modelAndFormValue$: Observable<GridRegistrationModel>;

  /**
   * Gesamt-Leistungen der Speicheranlage, live aus dem Protokoll gerechnet (`Anzahl × Einzelwert`).
   * Wird in der Karte „Netzanmeldung“ angezeigt und aktualisiert sich mit jeder Eingabe.
   */
  storageTotals$: Observable<{
    usableCapacity: number | null;
    dischargePower: number | null;
    chargePower: number | null;
    power: number | null;
  }>;

  mastrValidationActive$ = this.dataShareService
    .getSingleTenantConfig$<boolean>(
      'gridRegistration.mastrValidationConfig.enableMastrValidationForPlaceInServiceRequest'
    )
    .pipe(takeUntilDestroyed(this.destroyRef));

  mastrMismatchFields: string[] = [];

  /** Kalendertag (YYYY-MM-DD) aus dem MaStR nach dem letzten `updateFormWithMastrData`. */
  private readonly mastrPlaceInServiceDatumExpectedCalendarKey$ = new BehaviorSubject<string | null>(null);

  /** Warnung, wenn das Inbetriebnahmedatum vom MaStR-Datum abweicht (`null` = keine Warnung). */
  placeInServiceDatumMastrWarning$: Observable<string | null>;

  /** Nur zur Anzeige: Datumsangaben der Stromspeichereinheit aus dem MaStR. */
  readonly storageMaStRDisplayData = {
    inbetriebnahmeDatum: null as Date,
    registerDate: null as Date
  };

  get placeInForm() {
    return this.formGroup.get('placeInService') as FormGroup;
  }

  get producers() {
    return this.formGroup.controls['producers'] as FormArray;
  }

  get pvModules() {
    return this.formGroup.controls['pvModules'] as FormArray;
  }

  get storages() {
    return this.formGroup.controls['storages'] as FormArray;
  }
  // Wird verwendet, da momentan nur ein Speicher an einer Netzanmeldung hängen kann
  get firstStorage() {
    return this.storages.at(0) as StorageForm;
  }

  get consumers() {
    return this.formGroup.controls['consumers'] as FormArray;
  }
  // Speicher als Verbraucher
  get firstConsumer() {
    return this.consumers.at(0) as TypedForm<ConsumerModel>;
  }

  get firstProducer() {
    return this.producers.at(0) as ProducerForm | undefined;
  }

  get uploads(): FormArray {
    return this.formGroup.get('uploads') as FormArray;
  }

  get componentsCardHasError(): boolean {
    if (this.storages?.status === 'INVALID' || this.consumers?.status === 'INVALID') {
      return true;
    }
    return (
      StorageModel.hasProduction(this.firstStorage?.value?.storageOperationType) &&
      this.producers?.status === 'INVALID'
    );
  }

  get meterOne() {
    return this.formGroup.get('meterOne');
  }

  get meterTwo() {
    return this.formGroup.get('meterTwo');
  }

  get meterThree() {
    return this.formGroup.get('meterThree');
  }

  get meteringConcept() {
    return this.formGroup.get('meteringConcept.id') as FormControl;
  }

  get feederConnectionStandard() {
    return this.model.feederConnectionStandard;
  }

  /** Wie in gr-custom-approve: cos φ ist nur bei nicht-kennliniengeführten Verfahren sinnvoll. */
  get isQUStandardCurve(): boolean {
    return this.model?.reactivePowerProvisioningMethod === ReactivePowerProvisioningMethod.Q_U_STANDARD_CURVE;
  }

  get initialP14aApplies() {
    const firstConsumer = this.model?.consumers[0];
    if (!firstConsumer) {
      return false;
    }
    return p14aApplies('Storage', consumerForP14aCheck(firstConsumer, this.model?.storages[0]?.count));
  }

  /**
   * MaStR-Validierung greift nur, wenn die Prozesskonfiguration sie aktiviert **und** sie für
   * diese Netzanmeldung nicht individuell deaktiviert wurde (Aktion „MaStR-Validierung
   * deaktivieren“, z.B. im Status *Freigegeben*).
   */
  private get isMastrValidationActive(): boolean {
    return (
      !this.model?.disableMastrValidation &&
      !!this.dataShareService.tenantConfig?.gridRegistration?.mastrValidationConfig
        ?.enableMastrValidationForPlaceInServiceRequest
    );
  }

  /** Prozesskonfiguration: Darf das EEG-Inbetriebnahmedatum trotz MaStR-Abruf editiert werden? */
  private get isPlaceInServiceDateEditAllowed(): boolean {
    return !!this.dataShareService.tenantConfig?.gridRegistration?.mastrValidationConfig
      ?.allowPlaceInServiceDateEdit;
  }

  ngOnInit(): void {
    this.model = this.data.model;

    this.formGroup = this.fb.group(
      {
        deviceType: [null as number],
        placeInService: this.createFormsGrPlaceinServiceConfirmationService.createPlaceInServiceRequestForm(
          new PlaceInServiceModel(),
          // Bei aktiver MaStR-Validierung stammt das Inbetriebnahmedatum aus dem Register und ist
          // nur editierbar, wenn die Prozesskonfiguration das ausdrücklich erlaubt.
          this.isMastrValidationActive && !this.isPlaceInServiceDateEditAllowed
        ),
        newProduction: [null as number],
        newInstalledPower: [null as number],
        // Detaillierte Aufschlüsselung von Modulen / Wechselrichtern
        pvModules: this.fb.array<PVModuleForm>([]),
        producers: this.fb.array<ProducerForm>([]),
        storages: this.fb.array<StorageForm>([]),
        consumers: this.fb.array<ConsumerFormV2>([]),
        uploads: this.fb.array<UploadForm>([]),
        meterNumber: ['', Validators.required],
        meterOne: this.fb.group({ meterNo: [''], value: [''], obisCode: [''] }),
        meterTwo: this.fb.group({ meterNo: [''], value: [''], obisCode: [''] }),
        meterThree: this.fb.group({ meterNo: [''], value: [''], obisCode: [''] }),
        meteringConcept: this.fb.group({ id: [null as number, Validators.required] }),
        maStRNumberProducer: [''],
        maStRSSENumberStorage: [''],
        // Abfrageschlüssel der MaStR-Validierung: Im Speicher-Prozess steht die
        // Stromspeichereinheit im Vordergrund, nicht die Erzeugungseinheit.
        maStRNumberStorage: [
          '',
          ...(this.isMastrValidationActive
            ? [[Validators.required, Validators.pattern(RegexToCheckModuleMaStrNumber)]]
            : [])
        ],
        // Automatisch aus dem MaStR ermittelt
        maStREEGNumber: this.fb.control({ value: '', disabled: true }),
        maStRNumber: [''],
        maStRAcceptedDeclaration: [false],
        maStRRegistrationDate: this.fb.control({
          value: null as Date,
          disabled: this.isMastrValidationActive
        }),
        customer: this.createFormsService.createContactDataFormV2({}, false)
      },
      {
        // No PV module power threshold validator for storage-only place-in flow.
        updateOn: 'change'
      }
    );

    /*this.showFeedInManagement$ = this.dataShareService
      .getSingleTenantConfig$('gridRegistration.showFeedInManagementForPlaceInServiceRequest')
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        switchMap((config) => {
          if (!config) return of(false);
          return this.formGroup.controls.pvModules.valueChanges.pipe(
            startWith(this.formGroup.controls.pvModules.value),
            map((pvModules) => {
              const installedPower = pvModules
                .map((item) => item.count * item.modulePowerWatt)
                .reduce((acc, cur) => acc + cur, 0);
              return installedPower / 1000 > 25;
            })
          );
        }),
        distinctUntilChanged(),
        tap((value) => {
          const feedInManagementSerialNumberControl = this.formGroup.controls.feedInManagementSerialNumber;
          if (!value) {
            feedInManagementSerialNumberControl.setValidators(null);
          } else {
            feedInManagementSerialNumberControl.setValidators([Validators.required]);
          }
          feedInManagementSerialNumberControl.updateValueAndValidity();
        }),
        shareReplay(1)
      );*/
    this.formValue$ = this.formGroup.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.formGroup.value),
      shareReplay(1)
    );

    this.hasProduction$ = this.formValue$.pipe(
      map((formValue) => StorageModel.hasProduction(formValue.storages[0]?.storageOperationType))
    );

    this.modelAndFormValue$ = this.formValue$.pipe(
      map((formValue) => ({ ...this.model, ...formValue } as GridRegistrationModel))
    );

    // Alle Speicher-Einzelwerte sind pro Speichersystem erfasst → Gesamtwert = Anzahl × Einzelwert.
    this.storageTotals$ = this.formValue$.pipe(
      map(() => {
        const value = this.formGroup.getRawValue();
        const count = value.storages?.[0]?.count;
        return {
          usableCapacity: storageTotal(count, value.storages?.[0]?.usableCapacity),
          dischargePower: storageTotal(count, value.producers?.[0]?.dischargePower),
          chargePower: storageTotal(count, value.consumers?.[0]?.chargePower),
          power: storageTotal(count, value.consumers?.[0]?.power)
        };
      }),
      shareReplay(1)
    );

    const placeInServiceDatumControl = this.placeInForm.get('placeInServiceDatum');
    this.placeInServiceDatumMastrWarning$ = combineLatest([
      this.mastrPlaceInServiceDatumExpectedCalendarKey$,
      placeInServiceDatumControl.valueChanges.pipe(
        startWith(placeInServiceDatumControl.value),
        map((value) => placeInServiceDatumCalendarKey(value)),
        distinctUntilChanged()
      )
    ]).pipe(
      map(([expectedKey, currentKey]) =>
        expectedKey != null && currentKey !== expectedKey
          ? 'Das von Ihnen eingetragene Datum der EEG-technischen Inbetriebnahme stimmt nicht mit dem Datum im MaStR überein. Bitte stimmen Sie das Datum ggf. mit dem Anlagenbetreiber ab.'
          : null
      ),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef)
    );

    this.initShowExternalIdFields();
    if (this.model.producers && this.model.producers.length > 0 && !this.model.newProduction) {
      this.model.producers.forEach((x) => {
        this.totalActivePower = this.totalActivePower + x.acActivePowerMax * x.count;
        this.totalApparentPower = this.totalApparentPower + x.acApparentPowerMax * x.count;
      });

      /**
       * Handle Backwards compatibility
       * it is possible that we have old processes that don't have
       *   newProduction
       *   newInstalledPower
       *
       * in that case we want to patch it with the accumuilated valiue of the installed components
       */
      this.model.newProduction = this.totalApparentPower;
    }
    if (this.model.pvModules && this.model.pvModules.length > 0 && !this.model.newInstalledPower) {
      this.model.pvModules.forEach((x) => {
        this.totalModulePower = this.totalModulePower + (x.modulePowerWatt * x.count) / 1000;
      });

      /**
       * Handle Backwards compatibility
       * it is possible that we have old processes that don't have
       *   newProduction
       *   newInstalledPower
       *
       * in that case we want to patch it with the accumuilated valiue of the installed components
       */
      this.model.newInstalledPower = this.totalModulePower;

      //  this.setFieldValidator('effectiveModulePowerWatt', 'required', true, this.totalModulePower);
    }

    this.initArrayForms();

    // nochmal alles reinbügeln
    this.formGroup.patchValue(this.model, { emitEvent: true });

    this.prefillMeterNumberFromGridRegistration();
    this.configurePlaceInServiceConsumerValidators();
    this.syncProducersWithStorageOperationType();
    this.markComponentFormsAsTouched();
    this.firstStorage?.controls.storageOperationType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.syncProducersWithStorageOperationType();
        this.markComponentFormsAsTouched();
      });

    this.dataShareService.uiAdaptions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((uiAdaptions) => {
      const invoiceMeteringCheckDoneOptional =
        uiAdaptions.find(
          (adaption) =>
            adaption.anchor === 'gridRegistration.placeInServiceRequest.invoiceMeteringCheckDoneOptional'
        )?.value || 'false';
      const powerDirectionSensorCheckDone =
        uiAdaptions.find(
          (adaption) =>
            adaption.anchor === 'gridRegistration.placeInServiceRequest.powerDirectionSensorCheckDoneOptional'
        )?.value || 'false';

      if (!this.model.feederConnectionStandard || this.model.feederConnectionStandard === 'VDE4105') {
        this.setFieldValidator('effectiveAndRequestedValuesMatch', 'mustTrue');
        if (invoiceMeteringCheckDoneOptional === 'true' || invoiceMeteringCheckDoneOptional === 'show') {
          this.setFieldValidator('invoiceMeteringCheckDone', 'optional');
        } else {
          this.setFieldValidator('invoiceMeteringCheckDone', 'mustTrue');
        }
        this.setFieldValidator('vdeCertificateExists', 'mustTrue');
        if (powerDirectionSensorCheckDone === 'true' || powerDirectionSensorCheckDone === 'show') {
          this.setFieldValidator('powerDirectionSensorCheckDone', 'optional');
        } else {
          this.setFieldValidator('powerDirectionSensorCheckDone', 'mustTrue');
        }
        this.setFieldValidator('vdeArN4100_55_CertificateExists', 'required');
        this.setFieldValidator('powerProtectionCertificateExists', 'mustTrue');
        this.setFieldValidator('factorCosphiCheck', 'mustTrue');
        this.setFieldValidator('naProtection', 'required');
        this.setFieldValidator('overvoltageProtectionType', 'required');
        this.setFieldValidator('pMonitoringExists', 'required');
        this.setFieldValidator('eegConfirmation', 'optional');
        this.setFieldValidator('flawlessFunctionGuarantee', 'optional');
        this.setFieldValidator('symmetryConditions', 'required');
        this.setFieldValidator('placeInServiceDatum', 'required');
        this.setFieldValidator('vdeConditions', 'mustTrue');
      }

      // Bewusst außerhalb des VDE4105-Blocks: die zusätzliche Bedingung gilt für jeden
      // Anschlussstandard.
      this.updateCustomConditionsCheckboxValidation(
        uiAdaptions.find((adaption) => adaption.anchor === CUSTOM_CONDITIONS_CHECKBOX_ANCHOR)
      );
    });
  }

  /**
   * Setzt die Pflicht-Validierung der frei konfigurierbaren Bedingung, solange die zugehörige
   * UI-Adaption aktiv ist. Ist sie inaktiv oder nicht hinterlegt, bleibt die Checkbox ohne
   * Validator und blockiert das Absenden nicht.
   */
  private updateCustomConditionsCheckboxValidation(checkboxAdaption: UiAdaptionsModel | undefined): void {
    if (
      checkboxAdaption &&
      (checkboxAdaption.visibility === 'show' || checkboxAdaption.visibility === 'true')
    ) {
      this.customConditionsCheckbox.setValidators(this.validatorsService.checkboxRequiredValidator());
    } else {
      this.customConditionsCheckbox.clearValidators();
    }
    this.customConditionsCheckbox.updateValueAndValidity();
  }

  setValidatorsNaProtection() {
    if (this.placeInForm.get('naProtection')) {
      if (this.placeInForm.get('naProtection').value == NaProtectionTypeEnum.Integrated) {
        this.setFieldValidator('overvoltageReleaseTestDone', 'optional');
        this.setFieldValidator('triggerCircuitTestDone', 'optional');
      }
      if (this.placeInForm.get('naProtection').value == NaProtectionTypeEnum.Centralised) {
        this.setFieldValidator('overvoltageReleaseTestDone', 'mustTrue');
        this.setFieldValidator('triggerCircuitTestDone', 'mustTrue');
      }
      this.placeInForm.get('overvoltageReleaseTestDone').patchValue(null);
      this.placeInForm.get('triggerCircuitTestDone').patchValue(null);
      this.placeInForm.get('overvoltageReleaseTestDone').markAsTouched();
      this.placeInForm.get('triggerCircuitTestDone').markAsTouched();
    }
  }

  setValidatorsMonitoring() {
    if (this.placeInForm.get('pMonitoringExists')) {
      if (this.placeInForm.get('pMonitoringExists').value == false) {
        this.setFieldValidator('paveTestDone', 'optional');
        this.setFieldValidator('effectivePave', 'optional');
      }
      if (this.placeInForm.get('pMonitoringExists').value == true) {
        this.setFieldValidator('paveTestDone', 'mustTrue');
        this.setFieldValidator('effectivePave', 'required');
      }
      this.placeInForm.get('paveTestDone').patchValue(null);
      this.placeInForm.get('effectivePave').patchValue(null);
      this.placeInForm.get('paveTestDone').markAsTouched();
      this.placeInForm.get('effectivePave').markAsTouched();
    }
  }

  setSymmetryConditionsMonitoring() {
    if (this.placeInForm.get('symmetryConditions')) {
      if (
        this.placeInForm.get('symmetryConditions').value !=
        this.symmetryConditionsEnum.SinglePhaseDistribution
      ) {
        this.setFieldValidator('addedGenerationL1', 'optional');
        this.setFieldValidator('addedGenerationL2', 'optional');
        this.setFieldValidator('addedGenerationL3', 'optional');
      }
      if (
        this.placeInForm.get('symmetryConditions').value ==
        this.symmetryConditionsEnum.SinglePhaseDistribution
      ) {
        this.setFieldValidator('addedGenerationL1', 'required');
        this.setFieldValidator('addedGenerationL2', 'required');
        this.setFieldValidator('addedGenerationL3', 'required');
      }
      this.placeInForm.get('addedGenerationL1').patchValue(null);
      this.placeInForm.get('addedGenerationL2').patchValue(null);
      this.placeInForm.get('addedGenerationL3').patchValue(null);
      this.placeInForm.get('addedGenerationL1').markAsTouched();
      this.placeInForm.get('addedGenerationL2').markAsTouched();
      this.placeInForm.get('addedGenerationL3').markAsTouched();
    }
  }

  cancel() {
    this.onConfirm.emit(null);
  }

  confirm(hasMastrMismatch: boolean = false) {
    this.formGroup.markAllAsTouched();
    if (this.isValid() && !hasMastrMismatch) {
      this.syncMeterNumberToStorageConsumer();
      const result = {
        ...this.model,
        ...(this.formGroup.getRawValue() as unknown as Partial<GridRegistrationModel>)
      };
      this.onConfirm.emit(result);
    }
    if (this.hasMastrDataFieldMismatch()) {
      this.toastrService.error(
        'Bitte überprüfen Sie die Tabelle der Markt-Stammdatenregister-Daten. Die rot markierten Felder weisen auf Abweichungen hin, die behoben werden müssen.',
        'Abweichungen von Markt-Stammdatenregister vorhanden'
      );
    }
    if (this.hasInvalidMastrNumber()) {
      this.toastrService.error(
        'Bitte überprüfen Sie die angegebene Markt-Stammdatenregister-Nummer des Speichers',
        'Ungültige Markt-Stammdatenregister-Nummer'
      );
    }
  }

  setMastrMismatchFields(fields: string[]) {
    this.mastrMismatchFields = fields?.length ? fields : [];
  }

  /** True, wenn die MaStR-Nummer selbst ungültig, leer, im Ladezustand oder nicht abrufbar ist. */
  hasInvalidMastrNumber(): boolean {
    return this.mastrMismatchFields.includes(MASTR_NUMBER_MISMATCH_TAG);
  }

  /**
   * True, wenn echte Datenabweichungen (jenseits des reinen MaStR-Nummer-Status) vorliegen.
   * Steuert den Toast und die rote Markierung der Vergleichskarte.
   */
  hasMastrDataFieldMismatch(): boolean {
    return this.mastrMismatchFields.some((field) => field !== MASTR_NUMBER_MISMATCH_TAG);
  }

  hasMastrMismatchField(field: string): boolean {
    return this.mastrMismatchFields.includes(field);
  }

  /**
   * Übernimmt die vom Marktstammdatenregister gelieferten Daten der Stromspeichereinheit.
   *
   * Das Backend liefert die Speichereinheit in derselben DTO-Hülle wie eine PV-Einheit
   * (`pvUnit`), damit die Vergleichs- und Übernahmelogik geteilt werden kann — Registrierungs-
   * und Inbetriebnahmedatum sowie die EEG-Nummer stammen also aus `pvUnit`. Die Kennzahlen der
   * Speichereinheit selbst hängen unter `location.storageUnits[0]`.
   */
  updateFormWithMastrData(data: MastrPvPlantWithLocationDto) {
    const updateFormControl = (control: FormControl, value: unknown) => {
      if (control.disabled) {
        control.enable();
        control.setValue(value);
        control.disable();
      } else {
        control.setValue(value);
      }
    };

    updateFormControl(
      this.formGroup.controls.maStRRegistrationDate,
      data?.pvUnit?.registrierungsdatum ? new Date(data.pvUnit.registrierungsdatum) : null
    );
    const placeInServiceDatumControl = this.formGroup.controls.placeInService.controls.placeInServiceDatum;
    updateFormControl(
      placeInServiceDatumControl,
      data?.pvUnit?.inbetriebnahmedatum ? new Date(data.pvUnit.inbetriebnahmedatum) : null
    );
    this.mastrPlaceInServiceDatumExpectedCalendarKey$.next(
      placeInServiceDatumCalendarKey(placeInServiceDatumControl.value)
    );
    placeInServiceDatumControl.updateValueAndValidity({ emitEvent: true });
    updateFormControl(this.formGroup.controls.maStREEGNumber, data?.pvUnit?.eegMastrNummer || null);

    const storageData = data?.location?.storageUnits?.length ? data.location.storageUnits[0] : null;
    this.storageMaStRDisplayData.inbetriebnahmeDatum = storageData?.inbetriebnahmeDatum
      ? new Date(storageData.inbetriebnahmeDatum)
      : null;
    this.storageMaStRDisplayData.registerDate = storageData?.registerDate
      ? new Date(storageData.registerDate)
      : null;
  }

  setFieldValidator(
    fieldName: string,
    action: 'required' | 'optional' | 'mustTrue',
    valueLessOrEqual?: boolean,
    valueToCompare?: number
  ) {
    const selectedControl = this.placeInForm.get(fieldName);
    switch (action) {
      case 'required':
        selectedControl.setValidators(
          !valueLessOrEqual
            ? [Validators.required]
            : [Validators.required, this.lessOrEqual(fieldName, valueToCompare)]
        );
        break;
      case 'optional':
        selectedControl.clearValidators();
        break;
      case 'mustTrue':
        selectedControl.setValidators([Validators.required, this.createFormsService.mustTrue]);
        break;
    }
  }

  lessOrEqual(fieldName: string, valueToCompare: number): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      if (control.errors && !control.errors.lessOrEqual) {
        return;
      }
      if (control.value > valueToCompare) {
        return { lessOrEqual: true };
      } else {
        return null;
      }
    };
  }

  isValid(): boolean {
    this.loadErrorsList();
    this.customConditionsCheckbox.markAllAsTouched();
    return this.formGroup.valid && this.customConditionsCheckbox.valid;
  }

  loadErrorsList() {
    this.errorFormList = this.errorsFormsService.checkTwoLevelFormErrors(
      this.formGroup,
      ErrorFormCommissioningEnum,
      null
    );
  }

  private initArrayForms() {
    // PV-Module
    this.pvModules.clear();
    this.model.pvModules.forEach((pvModule) => {
      const pvModuleGroup = this.createFormsGridRegistrationService.createPVModuleFormSimple(pvModule);
      this.pvModules.push(pvModuleGroup);
    });
    // Erzeuger
    this.producers.clear();
    this.model.producers.forEach((producer) => {
      const producerGroup = this.createFormsGridRegistrationService.createProducerFormSimple(
        producer,
        this.model
      );
      this.producers.push(producerGroup);
    });
    // Speicher
    this.storages.clear();
    if (this.model.storages.length > 0) {
      this.model.storages.forEach((storage) => {
        const storageGroup = this.createFormsGridRegistrationService.createStorageFormV2(
          storage,
          this.model,
          false
        );
        this.storages.push(storageGroup);
      });
    }

    //Verbraucher
    this.consumers.clear();
    if (this.model.consumers.length > 0) {
      this.model.consumers.forEach((consumer, index) => {
        const consumerGroup = this.createFormsGridRegistrationService.createConsumerFormV2(consumer);
        this.consumers.push(consumerGroup);
        if (index === 0) {
          consumerGroup.setValidators(
            noP14aChange(this.initialP14aApplies, () => this.storages.at(0)?.value?.count)
          );
        }
      });
    }

    // Uploads
    this.uploads.clear();
    this.model.uploads.forEach((upload) => {
      const uploadFormGroup = this.createFormsService.createUploadForm(upload);
      this.uploads.push(uploadFormGroup);
    });
  }

  /**
   * Zählernummer aus der Netzanmeldung vorbelegen.
   *
   * Im v2-Speicherformular wird der Speicher als Verbraucher geführt; die Zählernummer hängt
   * deshalb an `consumers[0]` (`app-gr-meter-number-formpart`) und nicht an der Netzanmeldung
   * selbst. Ohne diesen Fallback bliebe das Feld im Inbetriebsetzungsprotokoll leer.
   */
  private prefillMeterNumberFromGridRegistration(): void {
    const meterNumberControl = this.formGroup.controls.meterNumber;
    if (meterNumberControl.value) {
      return;
    }
    const meterNumberFromStorageConsumer = this.model?.consumers?.[0]?.meterNumber;
    if (meterNumberFromStorageConsumer) {
      meterNumberControl.setValue(meterNumberFromStorageConsumer);
    }
  }

  /** Hält die Zählernummer am Speicher-Verbraucher mit dem Protokoll-Feld konsistent. */
  private syncMeterNumberToStorageConsumer(): void {
    const consumerMeterNumberControl = this.firstConsumer?.get('meterNumber');
    if (consumerMeterNumberControl) {
      consumerMeterNumberControl.setValue(this.formGroup.controls.meterNumber.value, { emitEvent: false });
    }
  }

  /** Only validate consumer fields that appear in storage-formpart-v2 for place-in-service. */
  private configurePlaceInServiceConsumerValidators(): void {
    const consumer = this.firstConsumer;
    if (!consumer) {
      return;
    }
    for (const field of ['manufacturer', 'count'] as const) {
      consumer.controls[field].clearValidators();
      consumer.controls[field].updateValueAndValidity({ emitEvent: false });
    }
  }

  private syncProducersWithStorageOperationType(): void {
    const hasProduction = StorageModel.hasProduction(this.firstStorage?.value?.storageOperationType);
    if (!hasProduction) {
      this.producers.clear();
      return;
    }
    if (this.producers.length === 0) {
      this.producers.push(
        this.createFormsGridRegistrationService.createProducerFormSimple(new ProducerModel(), this.model)
      );
    }
  }

  /** Align field-level errors with the components card, which flags INVALID without a touched check. */
  private markComponentFormsAsTouched(): void {
    this.storages.markAllAsTouched();
    this.consumers.markAllAsTouched();
    if (StorageModel.hasProduction(this.firstStorage?.value?.storageOperationType)) {
      this.producers.markAllAsTouched();
    }
  }

  private initShowExternalIdFields() {
    const config$ = this.dataShareService.tenantConfig$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((config) => config?.gridRegistration),
      shareReplay(1)
    );
    this.showExternalIdField$ = config$.pipe(map((config) => !!config?.showExternalIdField));
    this.showExternalCustomerIdField$ = config$.pipe(map((config) => !!config?.showExternalCustomerIdField));
  }

  readonly FeederConnectionStandard = FeederConnectionStandard;
  readonly of = of;
  readonly FeedingPlantTypeEnum = FeedingPlantTypeEnum;
  readonly MeteringConceptDisplayGroup = MeteringConceptDisplayGroup;

  get documentsUploadInfoUIAdaptionName() {
    return (
      'gridRegistration.' +
      this.model.gridRegistrationType.valueOf() +
      '.placeInServiceRequest.documentsUploadInfo'
    );
  }

  get mastrHintUIAdaptionName() {
    return 'gridRegistration.placeInServiceRequest.mastrHint';
  }
}

/** Gleicher Kalendertag in lokaler Zeit, ohne Uhrzeit (Datepicker vs. API-Datum). */
function placeInServiceDatumCalendarKey(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value as string | number);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function consumerForP14aCheck(consumer: ConsumerModel, storageCount?: number): ConsumerModel {
  return {
    ...consumer,
    count: consumer.count ?? storageCount ?? 0
  };
}

function noP14aChange(initialP14aApplies: boolean, storageCount: () => number | undefined): ValidatorFn {
  return (storageAsConsumerControl: AbstractControl<ConsumerModel>): ValidationErrors | null => {
    const currentP14aApplies = p14aApplies(
      'Storage',
      consumerForP14aCheck(storageAsConsumerControl.value, storageCount())
    );
    if (initialP14aApplies !== currentP14aApplies) {
      return currentP14aApplies ? { powerExceeded: true } : { powerDeceeded: true };
    } else return null;
  };
}
